<template>
    <div class="ms-page">
        <!-- ─────────────────── HEADER ─────────────────── -->
        <header class="ms-header">
            <div class="ms-header__inner">
                <div class="ms-header__title-wrap">
                    <h1 class="ms-title">קריאות שירות</h1>
                    <span class="ms-total-pill" :title="`סה״כ ${totalItems} קריאות`">
                        <v-icon size="15">mdi-headset</v-icon>
                        <span>{{ totalItems.toLocaleString('he-IL') }}</span>
                    </span>
                </div>

                <div class="ms-header__actions">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on }">
                            <button v-on="on" type="button" class="ms-refresh-btn" :disabled="progressShow"
                                @click="getAllRequests()" aria-label="רענון הרשימה">
                                <v-icon size="16" color="white"
                                    :class="{ 'ms-spin': progressShow }">mdi-refresh</v-icon>
                                <span>רענון</span>
                            </button>
                        </template>
                        <span>טעינה מחדש של הקריאות</span>
                    </v-tooltip>
                </div>
            </div>
        </header>

        <ProgressShow v-if="progressShow" v-model="progressShow"></ProgressShow>

        <!-- ─────────────────── MAIN ─────────────────── -->
        <main class="ms-main">
            <!-- STATUS SUMMARY (clickable filters) -->
            <section class="ms-summary-grid" aria-label="סינון לפי סטטוס">
                <button type="button" class="ms-summary-card ms-summary-card--all"
                    :class="{ 'ms-summary-card--selected': statusFilter === 'all' }" @click="setStatusFilter('all')">
                    <div class="ms-summary-card__icon"><v-icon size="19">mdi-headset</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">כל הקריאות</span>
                        <strong class="ms-summary-card__value">{{ counts.all.toLocaleString('he-IL') }}</strong>
                    </div>
                </button>

                <button v-for="s in statusOptions" :key="s.value" type="button" class="ms-summary-card" :class="[
                    `ms-summary-card--${s.modifier}`,
                    { 'ms-summary-card--selected': statusFilter === s.value }
                ]" @click="setStatusFilter(s.value)">
                    <div class="ms-summary-card__icon"><v-icon size="19">{{ s.icon }}</v-icon></div>
                    <div class="ms-summary-card__body">
                        <span class="ms-summary-card__label">{{ s.label }}</span>
                        <strong class="ms-summary-card__value">
                            {{ (counts[s.value] || 0).toLocaleString('he-IL') }}
                        </strong>
                    </div>
                </button>
            </section>

            <!-- TOOLBAR -->
            <div class="ms-toolbar">
                <div class="ms-search">
                    <v-icon class="ms-search__icon" size="19">mdi-magnify</v-icon>
                    <input v-model="search" type="text" class="ms-search__input"
                        placeholder="חיפוש לפי מספר קריאה, לקוח, ת״ז, מספר קו, מספר זמני או נושא…"
                        @input="handleSearchInput" />
                    <button v-if="search" type="button" class="ms-search__clear"
                        @click="search = ''; handleSearchInput()" aria-label="נקה חיפוש">
                        <v-icon size="14">mdi-close</v-icon>
                    </button>
                </div>

                <!-- PRIORITY FILTER -->
                <div class="ms-segment" role="group" aria-label="סינון לפי רמת דחיפות">
                    <span class="ms-segment__label">
                        <v-icon size="15">mdi-flag-outline</v-icon>
                        דחיפות
                    </span>
                    <button type="button" class="ms-segment__btn"
                        :class="{ 'ms-segment__btn--active': priorityFilter === 'all' }"
                        @click="setPriorityFilter('all')">הכל</button>
                    <button v-for="p in priorityOptions" :key="p.value" type="button" class="ms-segment__btn" :class="[
                        `ms-segment__btn--${p.modifier}`,
                        { 'ms-segment__btn--active': priorityFilter === p.value }
                    ]" @click="setPriorityFilter(p.value)">
                        <v-icon size="14">{{ p.icon }}</v-icon>
                        {{ p.label }}
                    </button>
                </div>

                <div class="ms-pagesize">
                    <label for="ms-pagesize-select">שורות</label>
                    <select id="ms-pagesize-select" class="ms-pagesize__select" v-model.number="itemsPerPage"
                        @change="onItemsPerPageChange">
                        <option v-for="n in [20, 50, 100, 200]" :key="n" :value="n">{{ n }}</option>
                    </select>
                </div>
            </div>

            <!-- ACTIVE FILTERS -->
            <div v-if="hasActiveFilters" class="ms-active-filters">
                <span class="ms-active-filters__label">סינון פעיל:</span>

                <button v-if="statusFilter !== 'all'" type="button" class="ms-chip" @click="setStatusFilter('all')">
                    סטטוס: {{ statusLabel(statusFilter) }}
                    <v-icon size="13">mdi-close</v-icon>
                </button>

                <button v-if="priorityFilter !== 'all'" type="button" class="ms-chip" @click="setPriorityFilter('all')">
                    דחיפות: {{ priorityLabel(priorityFilter) }}
                    <v-icon size="13">mdi-close</v-icon>
                </button>

                <button v-if="search" type="button" class="ms-chip" @click="search = ''; handleSearchInput()">
                    חיפוש: "{{ search }}"
                    <v-icon size="13">mdi-close</v-icon>
                </button>

                <button type="button" class="ms-chip ms-chip--reset" @click="resetFilters">
                    <v-icon size="14">mdi-filter-remove-outline</v-icon>
                    נקה הכל
                </button>
            </div>

            <!-- TABLE -->
            <div class="ms-table-card">
                <v-data-table :headers="headers" :items="requests" hide-default-footer :server-items-length="totalItems"
                    :items-per-page="itemsPerPage" :page.sync="currentPage" :sort-by.sync="sortBy"
                    :sort-desc.sync="sortDesc" @update:sort-by="onSortChange" @update:sort-desc="onSortChange"
                    @click:row="onRowClick" item-key="_id" class="ms-table ms-table--clickable">

                    <template v-for="header in headers" v-slot:[`header.${header.value}`]="{ header }">
                        <span class="ms-th">{{ header.text }}</span>
                    </template>

                    <template v-slot:item.request_number="{ item }">
                        <button type="button" class="ms-req-number" @click.stop="openViewDialog(item)"
                            :aria-label="`פתיחת קריאה ${item.request_number}`">
                            {{ item.request_number || '—' }}
                        </button>
                    </template>

                    <template v-slot:item.customer_id="{ item }">
                        <div class="ms-customer-cell">
                            <div class="ms-customer-cell__body">
                                <span class="ms-customer-cell__name">{{ getCustomerName(item) }}</span>
                                <span class="ms-customer-cell__cardid ms-selectable" @click.stop
                                    v-if="item.customer_id && item.customer_id.card_id">
                                    {{ item.customer_id.card_id }}
                                </span>
                            </div>
                            <v-tooltip bottom v-if="item.customer_id && item.customer_id._id">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-mini-btn"
                                        @click.stop="goToCustomerManuim(item)" aria-label="פתיחת דף מנויי הלקוח">
                                        <v-icon size="15">mdi-open-in-new</v-icon>
                                    </button>
                                </template>
                                <span>מעבר לדף מנויי הלקוח</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.line_number="{ item }">
                        <div class="ms-line-cell">
                            <span class="ms-number ms-selectable" @click.stop>{{ item.line_number || '—' }}</span>
                            <v-tooltip bottom v-if="item.temp_number">
                                <template v-slot:activator="{ on }">
                                    <span v-on="on" class="ms-temp-badge ms-selectable" @click.stop>
                                        <v-icon size="12">mdi-clock-fast</v-icon>
                                        {{ item.temp_number }}
                                    </span>
                                </template>
                                <span>מספר זמני שהתקבל בניוד</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.type="{ item }">
                        <span class="ms-type-badge">
                            <v-icon size="14">{{ typeIcon(item.type) }}</v-icon>
                            {{ typeLabel(item.type) }}
                        </span>
                    </template>

                    <template v-slot:item.supplier_name="{ item }">
                        <span v-if="item.supplier_name" class="ms-supplier">
                            <v-icon size="13">mdi-domain</v-icon>
                            {{ supplierLabel(item.supplier_name) }}
                        </span>
                        <span v-else class="ms-unassigned">—</span>
                    </template>

                    <template v-slot:item.seller_id="{ item }">
                        <span v-if="item.seller_id && item.seller_id.name" class="ms-seller">
                            <v-icon size="14">mdi-account-tie-outline</v-icon>
                            {{ item.seller_id.name }}
                        </span>
                        <span v-else class="ms-unassigned">—</span>
                    </template>

                    <template v-slot:item.status="{ item }">
                        <div class="ms-status-cell">
                            <span class="ms-status-pill" :class="`ms-status-pill--${statusModifier(item.status)}`">
                                <span class="ms-status-pill__dot"></span>
                                {{ statusLabel(item.status) }}
                            </span>

                            <v-tooltip bottom max-width="320" v-if="hasOpenNote(item)">
                                <template v-slot:activator="{ on }">
                                    <span v-on="on" class="ms-note-flag" @click.stop="openViewDialog(item)"
                                        aria-label="יש הערה פנימית">
                                        <v-icon size="13">mdi-exclamation-thick</v-icon>
                                    </span>
                                </template>
                                <span class="ms-note-tip">{{ item.internal_notes }}</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:item.files="{ item }">
                        <div class="ms-files-cell">
                            <v-tooltip bottom v-if="item.id_card_file && item.id_card_file.path">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-file-chip ms-file-chip--image"
                                        @click.stop="openFile(item.id_card_file)" aria-label="צפייה בתמונת תעודת זהות">
                                        <v-icon size="15">mdi-card-account-details-outline</v-icon>
                                    </button>
                                </template>
                                <span>תמונת ת״ז</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.supplier_form_file && item.supplier_form_file.path">
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-file-chip ms-file-chip--pdf"
                                        @click.stop="openFile(item.supplier_form_file)" aria-label="צפייה בטופס לספק">
                                        <v-icon size="15">mdi-file-pdf-box</v-icon>
                                    </button>
                                </template>
                                <span>טופס לספק</span>
                            </v-tooltip>

                            <v-tooltip bottom v-if="item.attachments && item.attachments.length">
                                <template v-slot:activator="{ on }">
                                    <span v-on="on" class="ms-file-chip ms-file-chip--more">
                                        <v-icon size="14">mdi-paperclip</v-icon>
                                        {{ item.attachments.length }}
                                    </span>
                                </template>
                                <span>{{ item.attachments.length }} קבצים נוספים</span>
                            </v-tooltip>

                            <span v-if="!hasAnyFile(item)" class="ms-files-cell__none">—</span>
                        </div>
                    </template>

                    <template v-slot:item.assigned_to="{ item }">
                        <span class="ms-assignee" v-if="item.assigned_to && item.assigned_to.name">
                            <span class="ms-assignee__avatar">{{ initials(item.assigned_to.name) }}</span>
                            {{ item.assigned_to.name }}
                        </span>
                        <span v-else class="ms-unassigned">לא הוקצה</span>
                    </template>

                    <template v-slot:item.createdAt="{ item }">
                        <div class="ms-date-cell">
                            <span class="ms-date-cell__main">{{ formatDateString(item.createdAt) }}</span>
                            <span class="ms-date-cell__rel">{{ relativeTime(item.createdAt) }}</span>
                        </div>
                    </template>

                    <template v-slot:item.actions="{ item }">
                        <div class="ms-actions">
                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--view"
                                        @click.stop="openViewDialog(item)" aria-label="צפייה בקריאה">
                                        <v-icon size="17">mdi-eye-outline</v-icon>
                                    </button>
                                </template>
                                <span>צפייה בפרטי הקריאה</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--edit"
                                        @click.stop="openEditDialog(item)" aria-label="עריכת קריאה">
                                        <v-icon size="17">mdi-pencil-outline</v-icon>
                                    </button>
                                </template>
                                <span>עריכת פרטי הקריאה</span>
                            </v-tooltip>

                            <v-tooltip bottom>
                                <template v-slot:activator="{ on }">
                                    <button v-on="on" type="button" class="ms-action-btn ms-action-btn--delete"
                                        @click.stop="openDeleteDialog(item)" aria-label="מחיקת קריאה">
                                        <v-icon size="17">mdi-delete-outline</v-icon>
                                    </button>
                                </template>
                                <span>מחיקת הקריאה</span>
                            </v-tooltip>
                        </div>
                    </template>

                    <template v-slot:no-data>
                        <div class="ms-empty">
                            <v-progress-linear v-if="progressShow" indeterminate color="#fdb721" height="3"
                                class="ms-empty__progress"></v-progress-linear>
                            <template v-else>
                                <div class="ms-empty__icon">
                                    <v-icon size="30">{{ hasActiveFilters ? 'mdi-filter-off-outline' :
                                        'mdi-headset-off' }}</v-icon>
                                </div>
                                <h3 class="ms-empty__title">
                                    {{ hasActiveFilters ? 'אין קריאות שתואמות לסינון' : 'אין קריאות שירות' }}
                                </h3>
                                <p class="ms-empty__hint">
                                    {{ hasActiveFilters
                                        ? 'שנה את הסינון או נקה אותו כדי לראות קריאות נוספות.'
                                        : 'קריאות חדשות ייכנסו לכאן אוטומטית עם הצטרפות מנויים.' }}
                                </p>
                                <button v-if="hasActiveFilters" type="button" class="ms-empty__btn"
                                    @click="resetFilters">
                                    <v-icon size="15">mdi-filter-remove-outline</v-icon>
                                    נקה סינון
                                </button>
                            </template>
                        </div>
                    </template>
                </v-data-table>

                <div v-if="totalItems" class="ms-pagination-wrap">
                    <span class="ms-pagination-info">
                        {{ rangeStart.toLocaleString('he-IL') }}–{{ rangeEnd.toLocaleString('he-IL') }}
                        מתוך {{ totalItems.toLocaleString('he-IL') }}
                    </span>
                    <v-pagination v-if="pageCount > 1" v-model="currentPage" circle :length="pageCount"
                        :total-visible="7" @input="onPageChange"></v-pagination>
                </div>
            </div>
        </main>

        <v-overlay :value="overlay">
            <v-progress-circular indeterminate size="84" color="#0d2c6d"></v-progress-circular>
        </v-overlay>

        <SnackBar v-model="snackbar" :snacbarText="snacbarText" :snackbarColorBt="snackbarColorBt"></SnackBar>

        <ViewRequestDialog v-model="viewDialog" v-if="viewDialog" :item="selectedItem" @editRequest="openEditDialog" />

        <EditRequestDialog v-model="editDialog" v-if="editDialog" :itemToEdit="selectedItem" @itemEdited="itemEdited" />

        <DeleteRequestDialog v-model="deleteDialog" v-if="deleteDialog" :item="selectedItem"
            @itemDeleted="itemDeleted" />
    </div>
</template>

<script>
import SnackBar from '@/components/widgets/snackBar.vue'
import ProgressShow from '@/components/widgets/progressShow.vue'
import ApiServices from '@/services/api.service'
import Utils from '@/util/utils'
import ViewRequestDialog from '@/components/admin/serviceRequests/dialogs/ViewRequestDialog.vue'
import EditRequestDialog from '@/components/admin/serviceRequests/dialogs/EditRequestDialog.vue'
import DeleteRequestDialog from '@/components/admin/serviceRequests/dialogs/DeleteRequestDialog.vue'
import SRConsts from '@/util/serviceRequestsConsts'
import _ from 'lodash'

export default {
    components: {
        SnackBar,
        ProgressShow,
        ViewRequestDialog,
        EditRequestDialog,
        DeleteRequestDialog,
    },
    data: () => ({
        headers: [
            { text: 'מס׳ קריאה', value: 'request_number' },
            { text: 'לקוח', value: 'customer_id', sortable: false },
            { text: 'מספר קו', value: 'line_number' },
            { text: 'סוג', value: 'type' },
            { text: 'ספק', value: 'supplier_name' },
            { text: 'משווק', value: 'seller_id', sortable: false },
            { text: 'סטטוס', value: 'status' },
            { text: 'קבצים', value: 'files', sortable: false },
            { text: 'מטפל', value: 'assigned_to', sortable: false },
            { text: 'נפתחה בתאריך', value: 'createdAt' },
            { text: 'פעולות', value: 'actions', sortable: false, align: 'center' },
        ],
        requests: [],
        totalItems: 0,
        counts: { all: 0, open: 0, in_progress: 0, completed: 0, canceled: 0 },

        currentPage: 1,
        itemsPerPage: 20,
        sortBy: 'createdAt',
        sortDesc: true,          // מהחדש לישן כברירת מחדל
        search: '',
        statusFilter: 'open',    // הדף נפתח על הקריאות שדורשות טיפול
        priorityFilter: 'all',

        progressShow: false,
        overlay: false,

        // dialogs
        viewDialog: false,
        editDialog: false,
        deleteDialog: false,
        selectedItem: {},

        // snackbar
        snackbar: false,
        snackbarColorBt: 'green',
        snacbarText: '',

        statusOptions: SRConsts.statusOptions,
        priorityOptions: SRConsts.priorityOptions,
    }),
    computed: {
        hasActiveFilters() {
            return this.statusFilter !== 'all' || this.priorityFilter !== 'all' || !!this.search;
        },
        pageCount() {
            if (!this.totalItems || !this.itemsPerPage) return 0;
            return Math.ceil(this.totalItems / this.itemsPerPage);
        },
        rangeStart() {
            if (!this.totalItems) return 0;
            return (this.currentPage - 1) * this.itemsPerPage + 1;
        },
        rangeEnd() {
            return Math.min(this.currentPage * this.itemsPerPage, this.totalItems);
        },
    },
    methods: {
        async getAllRequests(resetPage = false) {
            if (resetPage) this.currentPage = 1;
            try {
                this.progressShow = true;
                const token = localStorage.getItem("token");
                const api = process.env.VUE_APP_BASE_URL + `/service_requests/get_all_service_requests`;
                const query = new URLSearchParams({
                    page: this.currentPage,
                    itemsPerPage: this.itemsPerPage,
                    sortBy: this.sortBy || 'createdAt',
                    sortDesc: this.sortDesc ? 'desc' : 'asc',
                    search: (this.search || '').replace(/[\u200E\u200F\u202A-\u202E]/g, '').trim(),
                    status: this.statusFilter,
                    priority: this.priorityFilter,
                }).toString();

                const res = await fetch(`${api}?${query}`, ApiServices.requestOptions("GET", "", token));
                const jsonObject = await res.json();
                this.progressShow = false;

                if (res.status >= 400) {
                    this.showSnackBar("שגיאה בקבלת קריאות השירות", "red");
                } else if (res.status === 200) {
                    this.requests = jsonObject.data || [];
                    this.totalItems = jsonObject.totalItems || 0;
                    if (jsonObject.counts) {
                        this.counts = {
                            all: jsonObject.counts.all || 0,
                            open: jsonObject.counts.open || 0,
                            in_progress: jsonObject.counts.in_progress || 0,
                            completed: jsonObject.counts.completed || 0,
                            canceled: jsonObject.counts.canceled || 0,
                        };
                    }
                }
            } catch (error) {
                this.progressShow = false;
                this.showSnackBar("שגיאה בקבלת נתונים: " + error, "red");
            }
        },

        // ── Filters / search / paging ──
        setStatusFilter(value) {
            if (this.statusFilter === value) return;
            this.statusFilter = value;
            this.getAllRequests(true);
        },
        setPriorityFilter(value) {
            if (this.priorityFilter === value) return;
            this.priorityFilter = value;
            this.getAllRequests(true);
        },
        resetFilters() {
            this.statusFilter = 'all';
            this.priorityFilter = 'all';
            this.search = '';
            this.getAllRequests(true);
        },
        handleSearchInput() {
            this.debouncedGetAllRequests(true);
        },
        onPageChange(newPage) {
            this.currentPage = newPage;
            this.getAllRequests();
        },
        onItemsPerPageChange() {
            this.currentPage = 1;
            this.getAllRequests();
        },
        onSortChange() {
            this.getAllRequests();
        },

        // ── Display helpers ──
        statusLabel: SRConsts.statusLabel,
        statusModifier: SRConsts.statusModifier,
        priorityLabel: SRConsts.priorityLabel,   // עדיין בשימוש בצ'יפ הסינון הפעיל
        typeLabel: SRConsts.typeLabel,
        typeIcon: SRConsts.typeIcon,
        supplierLabel: SRConsts.supplierLabel,

        formatDateString(date) {
            return date ? Utils.formatDateString(date) : '—';
        },
        relativeTime(date) {
            if (!date) return '';
            const diff = Date.now() - new Date(date).getTime();
            if (isNaN(diff)) return '';
            const minutes = Math.floor(diff / 60000);
            if (minutes < 1) return 'עכשיו';
            if (minutes < 60) return `לפני ${minutes} דק׳`;
            const hours = Math.floor(minutes / 60);
            if (hours < 24) return `לפני ${hours} שע׳`;
            const days = Math.floor(hours / 24);
            if (days === 1) return 'אתמול';
            if (days < 30) return `לפני ${days} ימים`;
            const months = Math.floor(days / 30);
            if (months < 12) return `לפני ${months} חוד׳`;
            return `לפני ${Math.floor(months / 12)} שנים`;
        },
        getCustomerName(item) {
            if (!item.customer_id) return '—';
            const full = `${item.customer_id.name || ''} ${item.customer_id.family || ''}`.trim();
            return full || '—';
        },
        initials(name) {
            if (!name) return '?';
            return name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('');
        },
        hasOpenNote(item) {
            // סימון רק בקריאות פתוחות – בקריאה שהושלמה ההערה כבר לא דורשת תשומת לב
            return item.status === 'open' && !!(item.internal_notes || '').trim();
        },
        hasAnyFile(item) {
            return !!(
                (item.id_card_file && item.id_card_file.path) ||
                (item.supplier_form_file && item.supplier_form_file.path) ||
                (item.attachments && item.attachments.length)
            );
        },
        openFile(file) {
            const url = SRConsts.fileUrl(file);
            if (!url) return;
            window.open(url, '_blank', 'noopener');
        },
        goToCustomerManuim(item) {
            if (!item.customer_id || !item.customer_id._id) return;
            const cust = item.customer_id;
            const routeData = this.$router.resolve({
                name: "manuim",
                params: {
                    id: cust._id,
                    name: `${cust.name || ''} ${cust.family || ''}`.trim() || '—',
                    cardId: cust.card_id || '—'
                }
            });
            window.open(routeData.href, '_blank');
        },

        // ── Dialogs ──
        openViewDialog(item) {
            this.selectedItem = item;
            this.viewDialog = true;
        },
        onRowClick(item) {
            // אם המשתמש סימן טקסט בשורה – זו העתקה, לא לחיצה
            const selection = window.getSelection();
            if (selection && selection.toString().trim()) return;
            this.openViewDialog(item);
        },
        openEditDialog(item) {
            this.viewDialog = false;
            this.selectedItem = item;
            this.editDialog = true;
        },
        openDeleteDialog(item) {
            this.selectedItem = item;
            this.deleteDialog = true;
        },

        // ── Callbacks ──
        itemEdited(updated, text, color) {
            this.showSnackBar(text, color);
            if (!updated) return;
            const index = this.requests.findIndex(r => r._id === updated._id);
            if (index === -1) return;

            const prevStatus = this.requests[index].status;
            this.$set(this.requests, index, Object.assign({}, this.requests[index], updated));

            // עדכון המונים אם הסטטוס השתנה, בלי קריאה חוזרת לשרת
            if (updated.status && updated.status !== prevStatus) {
                if (this.counts[prevStatus] > 0) this.counts[prevStatus]--;
                this.counts[updated.status] = (this.counts[updated.status] || 0) + 1;
                // אם הקריאה יצאה מהסינון הנוכחי – נרענן כדי לא להציג שורה שלא תואמת
                if (this.statusFilter !== 'all' && updated.status !== this.statusFilter) {
                    this.getAllRequests();
                }
            }
        },
        itemDeleted(item, text, color) {
            if (!item) {
                this.showSnackBar(text, color);
                return;
            }
            const index = this.requests.findIndex(r => r._id === item._id);
            if (index !== -1) {
                this.requests.splice(index, 1);
                this.totalItems = Math.max(0, this.totalItems - 1);
                if (this.counts.all > 0) this.counts.all--;
                if (this.counts[item.status] > 0) this.counts[item.status]--;
            }
            this.showSnackBar(text, color);

            // אם התרוקן העמוד ואנחנו לא בראשון – חוזרים עמוד אחורה
            if (!this.requests.length && this.currentPage > 1) {
                this.currentPage--;
                this.getAllRequests();
            }
        },

        // ── Misc ──
        showSnackBar(message, color) {
            this.snacbarText = message;
            this.snackbarColorBt = color;
            this.snackbar = true;
        },
    },
    created() {
        this.debouncedGetAllRequests = _.debounce(this.getAllRequests, 700);
        this.getAllRequests();
    },
}
</script>

<style scoped>
.ms-page {
    --m-navy: #0d2c6d;
    --m-navy-soft: #142f78;
    --m-indigo: #3F51B5;
    --m-indigo-soft: rgba(63, 81, 181, 0.10);
    --m-purple: #8066f0;
    --m-purple-soft: rgba(128, 102, 240, 0.10);
    --m-gold: #fdb721;
    --m-blue: #1976d2;
    --m-blue-soft: rgba(25, 118, 210, 0.10);
    --m-surface: #ffffff;
    --m-bg: #f3f6f9;
    --m-border: #e3e8ef;
    --m-border-soft: #eef2f7;
    --m-text: #0f172a;
    --m-text-soft: #475569;
    --m-text-mute: #94a3b8;
    --m-green: #16804b;
    --m-green-soft: rgba(22, 128, 75, 0.10);
    --m-orange: #c0691c;
    --m-orange-soft: #fff3df;
    --m-red: #c74747;
    --m-red-soft: rgba(199, 71, 71, 0.10);
    --m-slate: #64748b;
    --m-slate-soft: rgba(100, 116, 139, 0.10);
    --m-shadow-sm: 0 1px 2px rgba(13, 44, 109, 0.04), 0 1px 3px rgba(13, 44, 109, 0.06);
    --m-shadow-md: 0 4px 14px rgba(13, 44, 109, 0.07), 0 2px 4px rgba(13, 44, 109, 0.04);
    --m-shadow-lg: 0 18px 40px -12px rgba(13, 44, 109, 0.18), 0 8px 16px -8px rgba(13, 44, 109, 0.10);
    --m-ease: cubic-bezier(0.22, 1, 0.36, 1);
    min-height: 100vh;
    padding-bottom: 60px;
    background: var(--m-bg);
    direction: rtl;
    font-family: 'Assistant', 'Heebo', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

/* ─────────────── HEADER ─────────────── */
.ms-header {
    background: linear-gradient(135deg, #0d2c6d 0%, #142f78 55%, #1a3a8a 100%);
    color: #fff;
    padding: 12px 0 14px;
    box-shadow: var(--m-shadow-md);
}

.ms-header__inner {
    max-width: 1700px;
    margin: 0 auto;
    padding: 0 28px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
}

.ms-header__title-wrap {
    display: flex;
    align-items: center;
    gap: 12px;
    min-width: 0;
}

.ms-header__actions {
    display: flex;
    align-items: center;
    gap: 10px;
}

.ms-title {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 800;
    letter-spacing: -0.01em;
    line-height: 1.2;
}

.ms-total-pill {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 4px 12px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.10);
    border: 1px solid rgba(255, 255, 255, 0.20);
    color: #fff;
    font-size: 0.85rem;
    font-weight: 700;
    font-feature-settings: "tnum" 1;
}

.ms-total-pill ::v-deep .v-icon {
    color: rgba(255, 255, 255, 0.85) !important;
}

.ms-refresh-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 14px;
    border-radius: 999px;
    background: rgba(253, 183, 33, 0.18);
    border: 1px solid rgba(253, 183, 33, 0.35);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.2s var(--m-ease);
}

.ms-refresh-btn:hover:not(:disabled) {
    background: rgba(253, 183, 33, 0.32);
    border-color: rgba(253, 183, 33, 0.55);
}

.ms-refresh-btn:disabled {
    opacity: 0.6;
    cursor: default;
}

.ms-spin {
    animation: ms-rotate 0.9s linear infinite;
}

@keyframes ms-rotate {
    to {
        transform: rotate(360deg);
    }
}

/* ─────────────── MAIN + SUMMARY ─────────────── */
.ms-main {
    max-width: 1700px;
    margin: 22px auto 0;
    padding: 0 22px;
}

.ms-summary-grid {
    display: grid;
    grid-template-columns: repeat(5, minmax(140px, 1fr));
    gap: 12px;
    margin-bottom: 18px;
}

.ms-summary-card {
    display: flex;
    align-items: center;
    gap: 11px;
    min-height: 72px;
    padding: 12px 14px;
    border-radius: 14px;
    background: var(--m-surface);
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-md);
    text-align: right;
    cursor: pointer;
    transition: all 0.2s var(--m-ease);
    font-family: inherit;
}

.ms-summary-card:hover {
    transform: translateY(-2px);
    box-shadow: var(--m-shadow-lg);
}

.ms-summary-card:focus-visible {
    outline: 2px solid var(--m-indigo);
    outline-offset: 2px;
}

.ms-summary-card--selected {
    border-color: var(--m-navy);
    background: linear-gradient(to bottom, rgba(13, 44, 109, 0.04), var(--m-surface));
    box-shadow: 0 0 0 2px rgba(13, 44, 109, 0.25), var(--m-shadow-md);
}

.ms-summary-card__icon {
    width: 39px;
    height: 39px;
    flex-shrink: 0;
    display: grid;
    place-items: center;
    border-radius: 11px;
}

.ms-summary-card--all .ms-summary-card__icon {
    color: var(--m-navy);
    background: rgba(13, 44, 109, 0.10);
}

.ms-summary-card--open .ms-summary-card__icon {
    color: var(--m-blue);
    background: var(--m-blue-soft);
}

.ms-summary-card--progress .ms-summary-card__icon {
    color: var(--m-orange);
    background: var(--m-orange-soft);
}

.ms-summary-card--completed .ms-summary-card__icon {
    color: var(--m-green);
    background: var(--m-green-soft);
}

.ms-summary-card--canceled .ms-summary-card__icon {
    color: var(--m-red);
    background: var(--m-red-soft);
}

.ms-summary-card__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
}

.ms-summary-card__label {
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--m-text-soft);
}

.ms-summary-card__value {
    font-size: 1.25rem;
    font-weight: 800;
    color: var(--m-text);
    font-feature-settings: "tnum" 1;
}

/* ─────────────── TOOLBAR ─────────────── */
.ms-toolbar {
    display: grid;
    grid-template-columns: 1fr auto auto;
    gap: 12px;
    align-items: center;
    padding: 12px 14px;
    border-radius: 12px;
    background: var(--m-surface);
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-sm);
    margin-bottom: 12px;
}

.ms-search {
    position: relative;
    display: flex;
    align-items: center;
}

.ms-search__icon {
    position: absolute;
    right: 10px;
    color: var(--m-text-mute);
}

.ms-search__input {
    width: 100%;
    padding: 8px 36px 8px 36px;
    border-radius: 10px;
    border: 1px solid var(--m-border);
    background: #fff;
    color: var(--m-text);
    font-size: 0.9rem;
    outline: none;
    transition: border-color 0.15s var(--m-ease), box-shadow 0.15s var(--m-ease);
}

.ms-search__input:focus {
    border-color: var(--m-indigo);
    box-shadow: 0 0 0 3px rgba(63, 81, 181, 0.15);
}

.ms-search__clear {
    position: absolute;
    left: 8px;
    display: grid;
    place-items: center;
    width: 22px;
    height: 22px;
    border-radius: 6px;
    background: transparent;
    color: var(--m-text-mute);
    cursor: pointer;
    border: none;
}

.ms-search__clear:hover {
    background: var(--m-border-soft);
    color: var(--m-text);
}

/* ── PRIORITY SEGMENT ── */
.ms-segment {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 3px;
    border-radius: 10px;
    background: #f6f8fb;
    border: 1px solid var(--m-border-soft);
}

.ms-segment__label {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 8px 0 4px;
    color: var(--m-text-soft);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.ms-segment__label ::v-deep .v-icon {
    color: var(--m-text-mute) !important;
}

.ms-segment__btn {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 5px 11px;
    border-radius: 8px;
    background: transparent;
    border: none;
    color: var(--m-text-soft);
    font-family: inherit;
    font-size: 0.79rem;
    font-weight: 700;
    cursor: pointer;
    white-space: nowrap;
    transition: all 0.15s var(--m-ease);
}

.ms-segment__btn:hover {
    background: #fff;
    color: var(--m-text);
}

.ms-segment__btn--active {
    background: var(--m-surface);
    color: var(--m-navy);
    box-shadow: var(--m-shadow-sm);
}

.ms-segment__btn--high.ms-segment__btn--active {
    color: var(--m-red);
}

.ms-segment__btn--low.ms-segment__btn--active {
    color: var(--m-slate);
}

.ms-pagesize {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: var(--m-text-soft);
    font-size: 0.85rem;
}

.ms-pagesize__select {
    padding: 5px 8px;
    border-radius: 8px;
    border: 1px solid var(--m-border);
    background: #fff;
    color: var(--m-text);
    outline: none;
    font-family: inherit;
}

/* ── ACTIVE FILTER CHIPS ── */
.ms-active-filters {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 7px;
    margin-bottom: 12px;
}

.ms-active-filters__label {
    color: var(--m-text-mute);
    font-size: 0.79rem;
    font-weight: 700;
}

.ms-chip {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 11px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    border: 1px solid rgba(63, 81, 181, 0.22);
    color: var(--m-indigo);
    font-family: inherit;
    font-size: 0.77rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-chip:hover {
    background: rgba(63, 81, 181, 0.18);
}

.ms-chip ::v-deep .v-icon {
    color: currentColor !important;
}

.ms-chip--reset {
    background: transparent;
    border-color: var(--m-border);
    color: var(--m-text-soft);
}

.ms-chip--reset:hover {
    background: var(--m-red-soft);
    border-color: rgba(199, 71, 71, 0.25);
    color: var(--m-red);
}

/* ─────────────── TABLE ─────────────── */
.ms-table-card {
    background: var(--m-surface);
    border-radius: 14px;
    border: 1px solid var(--m-border-soft);
    box-shadow: var(--m-shadow-md);
    overflow: hidden;
}

::v-deep .ms-table .v-data-table__wrapper {
    border-radius: 14px;
}

::v-deep .ms-table thead tr {
    background: linear-gradient(to bottom, #fafcff, #f3f6fa);
}

::v-deep .ms-table thead th {
    border-bottom: 1px solid var(--m-border) !important;
    color: var(--m-text-soft) !important;
    font-weight: 700 !important;
    font-size: 0.79rem !important;
    letter-spacing: 0.02em;
    white-space: nowrap;
}

::v-deep .ms-table tbody td {
    border-bottom: 1px solid var(--m-border-soft) !important;
    padding: 10px 12px !important;
    color: var(--m-text);
    font-size: 0.88rem;
    vertical-align: middle;
}

::v-deep .ms-table tbody tr:hover {
    background: rgba(63, 81, 181, 0.04) !important;
}

::v-deep .ms-table--clickable tbody tr {
    cursor: pointer;
}

/* שדות שצריך להיות אפשר לסמן ולהעתיק – לחיצה עליהם לא פותחת את הדיאלוג */
.ms-selectable {
    user-select: text;
    cursor: text;
    border-radius: 4px;
    transition: background 0.15s var(--m-ease);
}

.ms-selectable:hover {
    background: rgba(13, 44, 109, 0.06);
    box-shadow: 0 0 0 3px rgba(13, 44, 109, 0.06);
}

.ms-selectable::selection {
    background: rgba(63, 81, 181, 0.25);
}

.ms-th {
    display: inline-block;
    color: var(--m-text-soft);
    font-weight: 700;
    font-size: 0.79rem;
    letter-spacing: 0.02em;
}

/* ── REQUEST NUMBER ── */
.ms-req-number {
    padding: 3px 9px;
    border-radius: 7px;
    background: rgba(13, 44, 109, 0.07);
    border: 1px solid transparent;
    color: var(--m-navy);
    font-family: inherit;
    font-weight: 800;
    font-size: 0.85rem;
    font-feature-settings: "tnum" 1;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-req-number:hover {
    background: var(--m-navy);
    color: #fff;
}

/* ── CUSTOMER ── */
.ms-customer-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    max-width: 220px;
}

.ms-customer-cell__body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-width: 0;
}

.ms-customer-cell__name {
    font-weight: 700;
    color: var(--m-text);
    font-size: 0.88rem;
    line-height: 1.15;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 180px;
}

.ms-customer-cell__cardid {
    color: var(--m-text-mute);
    font-size: 0.72rem;
    font-weight: 600;
    line-height: 1.1;
    direction: ltr;
    font-feature-settings: "tnum" 1;
}

/* ── LINE NUMBER + TEMP ── */
.ms-line-cell {
    display: inline-flex;
    align-items: center;
    gap: 6px;
}

.ms-number {
    font-feature-settings: "tnum" 1;
    direction: ltr;
    color: var(--m-text);
    font-weight: 600;
}

.ms-temp-badge {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--m-purple-soft);
    color: var(--m-purple);
    font-size: 0.7rem;
    font-weight: 800;
    direction: ltr;
    cursor: help;
}

.ms-temp-badge ::v-deep .v-icon {
    color: var(--m-purple) !important;
}

/* ── TYPE / PRIORITY ── */
.ms-type-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.ms-type-badge ::v-deep .v-icon {
    color: var(--m-indigo) !important;
}

.ms-supplier {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 3px 10px;
    border-radius: 999px;
    background: var(--m-slate-soft);
    color: var(--m-slate);
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.ms-supplier ::v-deep .v-icon {
    color: var(--m-slate) !important;
}

.ms-seller {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-size: 0.84rem;
    font-weight: 600;
    color: var(--m-text);
    white-space: nowrap;
}

.ms-seller ::v-deep .v-icon {
    color: var(--m-text-mute) !important;
}

/* ── STATUS PILL ── */
.ms-status-cell {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.ms-note-flag {
    display: grid;
    place-items: center;
    width: 19px;
    height: 19px;
    border-radius: 50%;
    background: var(--m-orange-soft);
    border: 1px solid rgba(192, 105, 28, 0.35);
    color: var(--m-orange);
    cursor: pointer;
    animation: ms-note-pulse 2.4s ease-in-out infinite;
}

.ms-note-flag:hover {
    background: var(--m-orange);
    border-color: var(--m-orange);
    animation: none;
}

.ms-note-flag:hover ::v-deep .v-icon {
    color: #fff !important;
}

.ms-note-flag ::v-deep .v-icon {
    color: var(--m-orange) !important;
}

@keyframes ms-note-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 0 rgba(192, 105, 28, 0.35);
    }

    50% {
        box-shadow: 0 0 0 4px rgba(192, 105, 28, 0);
    }
}

.ms-note-tip {
    white-space: pre-wrap;
    font-size: 0.8rem;
    line-height: 1.5;
}

.ms-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 3px 10px;
    border-radius: 999px;
    font-size: 0.78rem;
    font-weight: 700;
    white-space: nowrap;
}

.ms-status-pill__dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: currentColor;
}

.ms-status-pill--open {
    background: var(--m-blue-soft);
    color: var(--m-blue);
}

.ms-status-pill--progress {
    background: var(--m-orange-soft);
    color: var(--m-orange);
}

.ms-status-pill--completed {
    background: var(--m-green-soft);
    color: var(--m-green);
}

.ms-status-pill--canceled {
    background: var(--m-red-soft);
    color: var(--m-red);
}

.ms-status-pill--progress .ms-status-pill__dot {
    animation: ms-pulse 1.8s ease-in-out infinite;
}

@keyframes ms-pulse {

    0%,
    100% {
        box-shadow: 0 0 0 3px rgba(192, 105, 28, 0.18);
    }

    50% {
        box-shadow: 0 0 0 5px rgba(192, 105, 28, 0.04);
    }
}

/* ── FILES ── */
.ms-files-cell {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.ms-files-cell__none {
    color: var(--m-text-mute);
}

.ms-file-chip {
    display: inline-flex;
    align-items: center;
    gap: 3px;
    min-width: 26px;
    height: 26px;
    padding: 0 7px;
    border-radius: 8px;
    border: 1px solid var(--m-border);
    background: #fff;
    color: var(--m-text-soft);
    font-family: inherit;
    font-size: 0.72rem;
    font-weight: 800;
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-file-chip--image:hover {
    background: var(--m-purple-soft);
    border-color: rgba(128, 102, 240, 0.3);
    color: var(--m-purple);
}

.ms-file-chip--pdf:hover {
    background: var(--m-red-soft);
    border-color: rgba(199, 71, 71, 0.3);
    color: var(--m-red);
}

.ms-file-chip--more {
    cursor: help;
    background: #f6f8fb;
}

/* ── ASSIGNEE ── */
.ms-assignee {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.84rem;
    font-weight: 600;
    color: var(--m-text);
    white-space: nowrap;
}

.ms-assignee__avatar {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    border-radius: 50%;
    background: var(--m-indigo-soft);
    color: var(--m-indigo);
    font-size: 0.7rem;
    font-weight: 800;
}

.ms-unassigned {
    color: var(--m-text-mute);
    font-size: 0.8rem;
    font-style: italic;
}

/* ── DATE ── */
.ms-date-cell {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
}

.ms-date-cell__main {
    color: var(--m-text);
    font-size: 0.83rem;
    font-weight: 600;
    direction: ltr;
    text-align: right;
    font-feature-settings: "tnum" 1;
}

.ms-date-cell__rel {
    color: var(--m-text-mute);
    font-size: 0.72rem;
}

/* ── ACTIONS ── */
.ms-actions {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    flex-wrap: nowrap;
}

.ms-action-btn {
    display: grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border-radius: 9px;
    background: transparent;
    border: 1px solid var(--m-border);
    color: var(--m-text-soft);
    cursor: pointer;
    transition: all 0.15s var(--m-ease);
}

.ms-action-btn--view:hover {
    background: var(--m-indigo);
    color: #fff;
    border-color: var(--m-indigo);
    box-shadow: 0 5px 12px -4px rgba(63, 81, 181, 0.60);
}

.ms-action-btn--edit:hover {
    background: var(--m-blue);
    color: #fff;
    border-color: var(--m-blue);
    box-shadow: 0 5px 12px -4px rgba(25, 118, 210, 0.55);
}

.ms-action-btn--delete:hover {
    background: var(--m-red);
    color: #fff;
    border-color: var(--m-red);
    box-shadow: 0 5px 12px -4px rgba(199, 71, 71, 0.60);
}

/* ── EMPTY + PAGINATION ── */
.ms-empty {
    padding: 50px 20px;
    text-align: center;
}

.ms-empty__progress {
    max-width: 320px;
    margin: 0 auto;
}

.ms-empty__icon {
    width: 62px;
    height: 62px;
    display: grid;
    place-items: center;
    margin: 0 auto 14px;
    border-radius: 50%;
    color: var(--m-indigo);
    background: var(--m-indigo-soft);
}

.ms-empty__title {
    margin: 0 0 4px;
    color: var(--m-text);
    font-size: 1.02rem;
}

.ms-empty__hint {
    margin: 0;
    color: var(--m-text-mute);
    font-size: 0.86rem;
}

.ms-empty__btn {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 14px;
    padding: 7px 16px;
    border-radius: 999px;
    background: var(--m-indigo-soft);
    border: 1px solid rgba(63, 81, 181, 0.22);
    color: var(--m-indigo);
    font-family: inherit;
    font-size: 0.82rem;
    font-weight: 700;
    cursor: pointer;
}

.ms-empty__btn:hover {
    background: var(--m-indigo);
    color: #fff;
}

.ms-empty__btn:hover ::v-deep .v-icon {
    color: #fff !important;
}

.ms-pagination-wrap {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 56px;
    padding: 14px 18px 12px;
    border-top: 1px solid var(--m-border-soft);
}

/* מחוץ לזרימה, כדי שהעימוד יישאר במרכז הכרטיס ולא במרכז השטח שנשאר */
.ms-pagination-info {
    position: absolute;
    inset-inline-start: 18px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--m-text-mute);
    font-size: 0.79rem;
    font-weight: 600;
    white-space: nowrap;
}

::v-deep .ms-pagination-wrap .v-pagination {
    margin: 0;
}

::v-deep .ms-pagination-wrap .v-pagination__item,
::v-deep .ms-pagination-wrap .v-pagination__navigation {
    border: 1px solid var(--m-border) !important;
    box-shadow: none !important;
}

::v-deep .ms-pagination-wrap .v-pagination__item--active {
    border-color: var(--m-navy) !important;
    background: var(--m-navy) !important;
    color: #fff !important;
    box-shadow: 0 4px 10px -2px rgba(13, 44, 109, 0.35) !important;
}

/* ─────────────── RESPONSIVE ─────────────── */
@media (max-width: 1350px) {
    .ms-toolbar {
        grid-template-columns: 1fr auto;
    }

    .ms-segment {
        grid-column: 1 / -1;
        justify-self: start;
        flex-wrap: wrap;
    }
}

@media (max-width: 1050px) {
    .ms-summary-grid {
        grid-template-columns: repeat(3, minmax(130px, 1fr));
    }
}

@media (max-width: 760px) {
    .ms-header__inner {
        align-items: stretch;
        flex-direction: column;
        padding: 0 18px;
        gap: 10px;
    }

    .ms-summary-grid {
        grid-template-columns: repeat(2, minmax(130px, 1fr));
    }

    .ms-main {
        margin-top: 16px;
        padding: 0 12px;
    }

    .ms-toolbar {
        grid-template-columns: 1fr;
    }

    .ms-pagesize {
        justify-content: flex-end;
    }

    .ms-pagination-wrap {
        flex-direction: column;
        gap: 10px;
    }

    /* במסך צר חוזרים לזרימה רגילה, אחרת הטקסט יתנגש בעימוד */
    .ms-pagination-info {
        position: static;
        transform: none;
    }
}

@media (prefers-reduced-motion: reduce) {

    .ms-status-pill--progress .ms-status-pill__dot,
    .ms-note-flag,
    .ms-spin {
        animation: none;
    }

    .ms-summary-card:hover {
        transform: none;
    }
}
</style>